import { memo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Link,
  Alert,
  CircularProgress,
  Stack,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { PRIVACY_LINKS } from './constants';

const CookieBannerContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(10px)',
  borderTop: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
  zIndex: 10000,
  borderRadius: 0,
  animation: 'slideUp 0.3s ease-out',
  '@keyframes slideUp': {
    from: {
      transform: 'translateY(100%)'
    },
    to: {
      transform: 'translateY(0)'
    }
  }
}));

const CookieContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1.5, 0)
  }
}));

const CookieActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1)
}));

const CookieButtons = styled(Stack)(({ theme }) => ({
  direction: 'row',
  spacing: 1,
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  [theme.breakpoints.down('md')]: {
    justifyContent: 'center',
    '& > *': {
      minWidth: '100px'
    }
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    '& > *': {
      width: '100%'
    }
  }
}));

const CookieBanner = memo(
  ({ onAcceptAll, onRejectAll, onCustomize, isLoading, error }) => {
    return (
      <CookieBannerContainer
        role="banner"
        aria-labelledby="cookie-banner-heading"
        aria-describedby="cookie-banner-description"
        elevation={8}
      >
        <Container maxWidth="xl">
          <CookieContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box flex={1}>
                <Typography
                  id="cookie-banner-heading"
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  🍪 We value your privacy
                </Typography>

                <Typography
                  id="cookie-banner-description"
                  variant="body2"
                  color="text.secondary"
                  paragraph
                  sx={{ lineHeight: 1.6 }}
                >
                  We use cookies and similar technologies to enhance your
                  browsing experience, analyze website traffic, and provide
                  personalized content. Your privacy matters to us - you have
                  full control over your cookie preferences.
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Please refer to our{' '}
                  <Link
                    href={PRIVACY_LINKS.cookiePolicy}
                    color="primary"
                    underline="hover"
                    aria-label="Read our Cookie Policy"
                  >
                    Cookie Policy
                  </Link>
                  {' • '}
                  <Link
                    href={PRIVACY_LINKS.privacyPolicy}
                    color="primary"
                    underline="hover"
                    aria-label="Read our Privacy Policy"
                  >
                    Privacy Policy
                  </Link>
                </Typography>
              </Box>

              {/* <Box> */}
              <CookieActions>
                {error && (
                  <Alert
                    severity="error"
                    size="small"
                    role="alert"
                    aria-live="polite"
                  >
                    {error}
                  </Alert>
                )}

                <CookieButtons
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={onRejectAll}
                    disabled={isLoading}
                    aria-label="Reject all optional cookies"
                    startIcon={
                      isLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                  >
                    Reject All
                  </Button>

                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={onCustomize}
                    disabled={isLoading}
                    aria-label="Customize cookie preferences"
                  >
                    Customize
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={onAcceptAll}
                    disabled={isLoading}
                    aria-label="Accept all cookies"
                    startIcon={
                      isLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                  >
                    Accept All
                  </Button>
                </CookieButtons>
              </CookieActions>
              {/* </Box> */}
            </Box>
          </CookieContent>
        </Container>
      </CookieBannerContainer>
    );
  }
);

CookieBanner.displayName = 'CookieBanner';

export default CookieBanner;
